import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChatPromptDto, ChatPromptListRequestDto } from "@/dto/ChatPromptDto";
import { superChatPromptApi } from "@/apis/superChatPrompt";
import { ResultListDto } from "@/dto/result";
import Router from "@/constants/router";

/**
 * 슈퍼 채팅 프롬프트 목록 Hook
 * @description 슈퍼 채팅 프롬프트 목록 관리 로직을 담당합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export default function useSuperChatPromptList() {
  const navigate = useNavigate();
  const [list, setList] = useState<ChatPromptDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<ChatPromptListRequestDto>({});

  /**
   * 프롬프트 목록 조회
   */
  const fetchPrompts = async (params?: ChatPromptListRequestDto) => {
    setIsLoading(true);
    try {
      console.log("[FETCH_PROMPTS]", {
        action: "start",
        params: params || filters,
        timestamp: new Date().toISOString(),
      });

      const requestParams = {
        page,
        limit: rowsPerPage,
        ...filters,
        ...params,
      };

      const response: ResultListDto<ChatPromptDto> =
        await superChatPromptApi.getList(requestParams);

      setList(response.list || []);
      setCount(response.count || 0);

      console.log("[FETCH_PROMPTS]", {
        action: "success",
        count: response.count,
        listLength: response.list?.length || 0,
      });
    } catch (error: any) {
      console.error("[FETCH_PROMPTS]", {
        action: "error",
        error: error.message,
      });

      // TODO: 에러 처리 (토스트 메시지 등)
      alert(`프롬프트 목록 조회 실패: ${error.message}`);
      setList([]);
      setCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 검색 핸들러
   */
  const handleSearch = (searchParams: ChatPromptListRequestDto) => {
    console.log("[SEARCH_PROMPTS]", {
      action: "search",
      params: searchParams,
    });

    setFilters(searchParams);
    setPage(1);
    fetchPrompts({ ...searchParams, page: 1 });
  };

  /**
   * 필터 핸들러
   */
  const handleFilter = (filterParams: ChatPromptListRequestDto) => {
    console.log("[FILTER_PROMPTS]", {
      action: "filter",
      params: filterParams,
    });

    setFilters(filterParams);
    setPage(1);
    fetchPrompts({ ...filterParams, page: 1 });
  };

  /**
   * 페이지 변경 핸들러 (TablePagination 방식)
   */
  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const actualPage = newPage + 1; // MUI는 0-based, API는 1-based
    console.log("[PAGE_CHANGE]", {
      action: "page_change",
      muiPage: newPage,
      actualPage,
    });

    setPage(actualPage);
    fetchPrompts({ ...filters, page: actualPage });
  };

  /**
   * 페이지당 항목 수 변경 핸들러
   */
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log("[ROWS_PER_PAGE_CHANGE]", {
      action: "rows_per_page_change",
      newRowsPerPage,
    });

    // 페이지당 항목 수 변경 시 첫 페이지로 이동
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchPrompts({ ...filters, page: 1, limit: newRowsPerPage });
  };

  /**
   * 프롬프트 상세 보기 핸들러
   */
  const handleView = (prompt: ChatPromptDto) => {
    console.log("[VIEW_PROMPT]", {
      action: "view",
      promptId: prompt.promptId,
      promptKey: prompt.promptKey,
    });

    navigate(
      Router.SuperChatPromptDetail.replace(":id", prompt.promptId.toString())
    );
  };

  /**
   * 프롬프트 삭제 핸들러
   */
  const handleDelete = async (promptId: number) => {
    console.log("[DELETE_PROMPT]", {
      action: "delete",
      promptId,
    });

    const confirmed = confirm("정말로 이 프롬프트를 삭제하시겠습니까?");
    if (!confirmed) return;

    try {
      await superChatPromptApi.delete(promptId);

      // 목록 새로고침
      await fetchPrompts();

      console.log("[DELETE_PROMPT]", {
        action: "success",
        promptId,
      });

      alert("프롬프트가 성공적으로 삭제되었습니다.");
    } catch (error: any) {
      console.error("[DELETE_PROMPT]", {
        action: "error",
        error: error.message,
        promptId,
      });

      alert(`프롬프트 삭제 실패: ${error.message}`);
    }
  };

  /**
   * 일괄 처리 핸들러
   */
  const handleBulkAction = async (action: string, ids: number[]) => {
    console.log("[BULK_ACTION]", {
      action,
      ids,
    });

    const confirmed = confirm(
      `선택한 ${ids.length}개의 프롬프트를 ${action}하시겠습니까?`
    );
    if (!confirmed) return;

    try {
      await superChatPromptApi.bulkUpdate(action, ids);

      // 목록 새로고침
      await fetchPrompts();

      console.log("[BULK_ACTION]", {
        action: "success",
        bulkAction: action,
        ids,
      });

      alert(`선택한 프롬프트들이 성공적으로 ${action}되었습니다.`);
    } catch (error: any) {
      console.error("[BULK_ACTION]", {
        action: "error",
        error: error.message,
        bulkAction: action,
        ids,
      });

      alert(`일괄 처리 실패: ${error.message}`);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPrompts();
  }, []);

  return {
    list,
    isLoading,
    page,
    count,
    rowsPerPage,
    filters,
    handleSearch,
    handleFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleView,
    handleDelete,
    handleBulkAction,
    refetch: fetchPrompts,
  };
}
